/*
  VCC Advanced Footer — Club Ledger
  Drawer behavior, theme inheritance, accessibility and Theme Editor lifecycle.
  The compact directory owns a same-URL history layer so browser Back closes it.
*/

(() => {
  const ROOT_SELECTOR = '[data-vcc-advanced-footer]';
  const FOCUSABLE_SELECTOR = [
    'a[href]:not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');
  const FOOTER_HISTORY_KEY = 'vccAdvancedFooter';
  const PRIVACY_HISTORY_KEY = 'vccFooterPrivacy';
  const PRIVACY_PERMISSIONS = {
    preferences: 'preferencesProcessingAllowed',
    analytics: 'analyticsProcessingAllowed',
    marketing: 'marketingAllowed',
    sale_of_data: 'saleOfDataAllowed'
  };
  const PRIVACY_UNAVAILABLE_MESSAGE = 'Cookie options unavailable. Privacy settings or a blocker may be preventing access.';
  let customerPrivacyPromise = null;
  let sharedTrustAssetsPromise = null;
  const customerPrivacyReady = (api) => ['currentVisitorConsent', 'setTrackingConsent', 'saleOfDataRegion',
    ...Object.values(PRIVACY_PERMISSIONS)].every((method) => typeof api?.[method] === 'function');

  const loadCustomerPrivacy = () => {
    const available = window.Shopify?.customerPrivacy;
    if (customerPrivacyReady(available)) return Promise.resolve(available);
    if (customerPrivacyPromise) return customerPrivacyPromise;
    const attempt = new Promise((resolve, reject) => {
      let finished = false;
      let requested = false;
      let featureError = null;
      let pollTimer = null;
      let deadlineTimer = null;
      const finish = (error, api) => {
        if (finished) return;
        finished = true;
        window.clearTimeout(pollTimer);
        window.clearTimeout(deadlineTimer);
        if (error) reject(error);
        else resolve(api);
      };
      const resolveReady = () => {
        if (finished) return true;
        const api = window.Shopify?.customerPrivacy;
        if (!customerPrivacyReady(api)) return false;
        finish(null, api);
        return true;
      };
      const observe = () => {
        if (resolveReady()) return;
        if (!requested && typeof window.Shopify?.loadFeatures === 'function') {
          requested = true;
          try {
            window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
              featureError = error || null;
              // A usable native API wins over a stale loader callback or error.
              resolveReady();
            });
          } catch (error) {
            featureError = error;
            resolveReady();
          }
        }
        if (!finished) pollTimer = window.setTimeout(observe, 100);
      };
      deadlineTimer = window.setTimeout(() => {
        if (resolveReady()) return;
        finish(new Error(featureError ? 'Privacy tools are unavailable.' : 'Privacy loading timed out.'));
      }, 8000);
      observe();
    });
    customerPrivacyPromise = attempt;
    const release = () => { if (customerPrivacyPromise === attempt) customerPrivacyPromise = null; };
    attempt.then(release, release);
    return attempt;
  };

  const absoluteUrl = (url) => {
    try {
      return new URL(url, document.baseURI).href;
    } catch (_error) {
      return url;
    }
  };

  const loadSharedTrustStylesheet = (url) => {
    if (!url) return Promise.reject(new Error('Trust stylesheet URL is unavailable.'));
    const targetUrl = absoluteUrl(url);
    const existing = [...document.querySelectorAll('link[rel="stylesheet"]')]
      .find((link) => absoluteUrl(link.href) === targetUrl);
    if (existing?.sheet) return Promise.resolve(existing);

    return new Promise((resolve, reject) => {
      const link = existing || document.createElement('link');
      const timeout = window.setTimeout(() => {
        reject(new Error('Trust stylesheet timed out.'));
      }, 8000);
      if (!existing) {
        link.rel = 'stylesheet';
        link.href = targetUrl;
      }
      link.addEventListener('load', () => resolve(link), { once: true });
      link.addEventListener('error', () => {
        window.clearTimeout(timeout);
        if (!existing) link.remove();
        reject(new Error('Trust stylesheet failed to load.'));
      }, { once: true });
      link.addEventListener('load', () => window.clearTimeout(timeout), { once: true });
      if (!existing) document.head.appendChild(link);
    });
  };

  const waitForSharedTrustReady = () => {
    if (document.documentElement.dataset.vccSharedTrustReady === 'true') return Promise.resolve();
    return new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        document.removeEventListener('vcc:shared-trust:ready', onReady);
        reject(new Error('Trust controller readiness timed out.'));
      }, 8000);
      const onReady = () => {
        window.clearTimeout(timeout);
        resolve();
      };
      document.addEventListener('vcc:shared-trust:ready', onReady, { once: true });
    });
  };

  const loadSharedTrustScript = (url) => {
    if (!url) return Promise.reject(new Error('Trust script URL is unavailable.'));
    const targetUrl = absoluteUrl(url);
    const existing = [...document.scripts].find((script) => absoluteUrl(script.src) === targetUrl);
    if (existing) return waitForSharedTrustReady().then(() => existing);

    const script = document.createElement('script');
    const loaded = new Promise((resolve, reject) => {
      script.src = targetUrl;
      script.async = true;
      script.addEventListener('load', () => resolve(script), { once: true });
      script.addEventListener('error', () => {
        script.remove();
        reject(new Error('Trust script failed to load.'));
      }, { once: true });
    });
    const ready = waitForSharedTrustReady();
    document.head.appendChild(script);
    return Promise.all([loaded, ready]).then(() => script);
  };

  class VccAdvancedFooter {
    constructor(root) {
      this.root = root;
      this.historyId = root.id || `vcc-advanced-footer-${root.dataset.sectionId || 'footer'}`;
      this.ownsHistoryEntry = false;
      this.historyTraversalPending = false;
      this.drawer = root.querySelector('[data-vcc-af-drawer]');
      this.openers = [...root.querySelectorAll('[data-vcc-af-open]')];
      this.closers = [...root.querySelectorAll('[data-vcc-af-close]')];
      this.backdrop = root.querySelector('[data-vcc-af-backdrop]');
      this.compact = root.querySelector('.vcc-af__compact');
      this.reviewTriggers = [...root.querySelectorAll('[data-vcc-af-review-trigger]')];
      this.trustTriggers = [...root.querySelectorAll('[data-vcc-af-trust-trigger]')];
      this.cookieButtons = [...root.querySelectorAll('[data-vcc-af-cookie-preferences]')];
      this.privacyDialog = root.querySelector('[data-vcc-af-privacy-dialog]');
      this.privacyToken = null;
      this.privacyPendingBack = false;
      this.reviewFallbackUrl = root.dataset.reviewFallbackUrl || '';
      this.pageWrapper = root.closest('.page-wrapper') || document.querySelector('.page-wrapper');
      this.lastTrigger = null;
      this.isOpen = false;
      this.currentMode = 'inline';
      this.media = window.matchMedia('(max-width: 749px)');
      this.historyMedia = window.matchMedia('(max-width: 1279.98px)');
      this.themeObserver = null;
      this.trustObserver = null;
      this.trustRoot = [...document.querySelectorAll('[data-vcc-shared-trust-root]')]
        .find((element) => element.dataset.vccSharedTrustRoot === root.dataset.vccAfTrustRoot) || null;
      this.trustPending = false;
      this.trustReplayTarget = null;
      this.trustRequestToken = null;
      this.backgroundState = [];
      this.focusTimer = null;
      this.bound = {};
      this.drawerEnabled = Boolean(this.drawer && this.openers.length);

      this.bind();
      this.resolveTheme();
      this.observeTheme();

      if (!this.drawerEnabled) {
        this.root.dataset.open = 'false';
        return;
      }

      this.updateMode();
      this.setInitialState();
      this.reconcileHistoryState();
    }

    bind() {
      this.bound.open = (event) => {
        event.preventDefault();
        this.lastTrigger = event.currentTarget;
        this.toggle();
      };
      this.bound.close = (event) => {
        event.preventDefault();
        this.close();
      };
      this.bound.keydown = (event) => this.onKeydown(event);
      this.bound.resize = () => this.updateMode();
      this.bound.themeEvent = (event) => this.onThemeEvent(event);
      this.bound.linkClick = (event) => this.onLinkClick(event);
      this.bound.reviewRequest = (event) => this.onReviewRequest(event);
      this.bound.trustRequest = (event) => this.onTrustRequest(event);
      this.bound.cookiePreferences = (event) => this.onCookiePreferences(event);
      this.bound.reviewClose = () => this.syncModalIsolation();
      this.bound.popstate = (event) => this.onPopState(event);

      this.cookieButtons.forEach((button) => button.addEventListener('click', this.bound.cookiePreferences));
      this.bindPrivacy();

      if (this.drawerEnabled) {
        this.openers.forEach((button) => button.addEventListener('click', this.bound.open));
        this.closers.forEach((button) => button.addEventListener('click', this.bound.close));
        this.backdrop?.addEventListener('click', this.bound.close);
        document.addEventListener('keydown', this.bound.keydown);
        this.root.addEventListener('click', this.bound.linkClick);
        this.reviewTriggers.forEach((button) => button.addEventListener('click', this.bound.reviewRequest));
        this.trustTriggers.forEach((link) => link.addEventListener('click', this.bound.trustRequest));

        if (this.media.addEventListener) this.media.addEventListener('change', this.bound.resize);
        else this.media.addListener(this.bound.resize);

        document.addEventListener('vcc:review-viewer:close', this.bound.reviewClose);
        window.addEventListener('popstate', this.bound.popstate, { capture: true });
        this.observeTrustDrawer();
      }

      document.addEventListener('vcc:theme-change', this.bound.themeEvent);
      document.addEventListener('vcc:site-theme-change', this.bound.themeEvent);
      document.addEventListener('theme:change', this.bound.themeEvent);
      document.addEventListener('color-scheme:change', this.bound.themeEvent);
    }

    setInitialState() {
      const desktopOpen = this.root.dataset.desktopInitialOpen === 'true';
      const mobileOpen = this.root.dataset.mobileInitialOpen === 'true';
      const feedback = this.root.querySelector('[data-vcc-af-form-feedback]');
      const shouldOpen = Boolean(feedback) || (this.media.matches ? mobileOpen : desktopOpen);
      this.setOpen(shouldOpen, {
        focus: Boolean(feedback) || (shouldOpen && this.currentMode === 'sheet'),
        focusTarget: feedback,
        announce: false,
        manageHistory: false
      });
    }

    updateMode() {
      const previousMode = this.currentMode;
      const configured = this.media.matches
        ? this.root.dataset.mobileMode
        : this.root.dataset.desktopMode;

      this.currentMode = configured === 'sheet' ? 'sheet' : 'inline';
      this.root.classList.toggle('vcc-af--mode-sheet', this.currentMode === 'sheet');
      this.root.classList.toggle('vcc-af--mode-inline', this.currentMode === 'inline');
      this.drawer.setAttribute('role', this.currentMode === 'sheet' ? 'dialog' : 'region');
      if (this.currentMode === 'sheet') this.drawer.setAttribute('aria-modal', 'true');
      else this.drawer.removeAttribute('aria-modal');
      this.syncBackdropState();
      this.syncModalIsolation();

      if (previousMode !== this.currentMode && this.isOpen) {
        this.syncScrollLock();
        if (this.currentMode === 'sheet') this.scheduleOpenFocus();
      }
    }

    toggle() {
      if (this.isOpen) this.close();
      else this.open();
    }

    open() {
      this.setOpen(true);
    }

    close() {
      if (!this.isOpen || this.historyTraversalPending) return;
      if (this.currentHistoryIsOwned()) {
        this.historyTraversalPending = true;
        try {
          window.history.back();
          return;
        } catch (_error) {
          this.historyTraversalPending = false;
        }
      }
      this.ownsHistoryEntry = false;
      this.setOpen(false, { manageHistory: false });
    }

    currentHistoryIsOwned(state = window.history.state) {
      return Boolean(state && typeof state === 'object' && state[FOOTER_HISTORY_KEY] === this.historyId);
    }

    historyIsEligible() {
      return this.root.dataset.compactStyle === 'night_arcade' && this.historyMedia.matches;
    }

    retireCurrentHistoryMarker() {
      if (!this.currentHistoryIsOwned() || !window.history?.replaceState) return;
      const nextState = { ...window.history.state };
      delete nextState[FOOTER_HISTORY_KEY];
      try {
        window.history.replaceState(nextState, '', window.location.href);
      } catch (_error) {
        return;
      }
      this.ownsHistoryEntry = false;
      this.historyTraversalPending = false;
    }

    pushHistoryEntry() {
      if (!this.historyIsEligible()) return;
      if (this.currentHistoryIsOwned()) {
        this.ownsHistoryEntry = true;
        return;
      }
      if (!window.history?.pushState) return;
      const currentState = window.history.state && typeof window.history.state === 'object'
        ? { ...window.history.state }
        : {};
      try {
        window.history.pushState({ ...currentState, [FOOTER_HISTORY_KEY]: this.historyId }, '', window.location.href);
        this.ownsHistoryEntry = true;
      } catch (_error) {
        this.ownsHistoryEntry = false;
      }
    }

    reconcileHistoryState() {
      if (!this.currentHistoryIsOwned()) return;
      if (!this.historyIsEligible()) {
        this.retireCurrentHistoryMarker();
        return;
      }
      this.ownsHistoryEntry = true;
      if (!this.isOpen) {
        this.setOpen(true, { focus: false, announce: false, manageHistory: false });
      }
    }

    onPopState(event) {
      if (!this.drawerEnabled) return;
      const destinationState = event.state && typeof event.state === 'object' ? event.state : {};
      if (!this.ownsHistoryEntry && !this.currentHistoryIsOwned(destinationState)) return;
      this.historyTraversalPending = false;
      if (this.currentHistoryIsOwned(destinationState)) {
        if (!this.historyIsEligible()) {
          this.retireCurrentHistoryMarker();
          if (this.isOpen) this.setOpen(false, { focus: false, manageHistory: false });
          return;
        }
        this.ownsHistoryEntry = true;
        if (!this.isOpen) {
          this.setOpen(true, { focus: false, manageHistory: false });
        }
        return;
      }

      this.ownsHistoryEntry = false;
      if (this.isOpen) this.setOpen(false, { manageHistory: false });
    }

    setOpen(next, options = {}) {
      const { focus = true, focusTarget = null, announce = true, manageHistory = true } = options;
      const wasOpen = this.isOpen;
      this.isOpen = Boolean(next);
      if (this.isOpen && !wasOpen && manageHistory) this.pushHistoryEntry();
      if (!this.isOpen && this.trustPending) this.cancelPendingTrustRequest();
      this.root.classList.toggle('is-open', this.isOpen);
      this.root.dataset.open = String(this.isOpen);
      this.openers.forEach((button) => button.setAttribute('aria-expanded', this.isOpen ? 'true' : 'false'));

      if (this.isOpen) {
        this.drawer.removeAttribute('inert');
        this.drawer.setAttribute('aria-hidden', 'false');
        this.syncBackdropState();
        this.syncModalIsolation();
        this.syncScrollLock();
      } else {
        this.syncBackdropState();
        this.syncModalIsolation();

        if (focus && wasOpen) {
          const returnTarget = this.lastTrigger || this.openers[0];
          if (returnTarget && document.contains(returnTarget)) {
            returnTarget.focus({ preventScroll: false });
          }
        }

        this.drawer.setAttribute('inert', '');
        this.drawer.setAttribute('aria-hidden', 'true');
        this.syncScrollLock();
      }

      window.clearTimeout(this.focusTimer);
      if (focus) {
        if (this.isOpen) {
          this.scheduleOpenFocus(focusTarget);
        }
      }

      if (announce) {
        this.root.dispatchEvent(new CustomEvent(
          this.isOpen ? 'vcc:advanced-footer:open' : 'vcc:advanced-footer:close',
          { bubbles: true, detail: { sectionId: this.root.dataset.sectionId } }
        ));
      }
    }

    syncScrollLock() {
      const shouldLock = this.isOpen && this.currentMode === 'sheet' && this.root.dataset.lockScroll !== 'false';
      document.documentElement.classList.toggle('vcc-af-scroll-lock', shouldLock);
      document.body.classList.toggle('vcc-af-scroll-lock', shouldLock);
      this.pageWrapper?.classList.toggle('vcc-af-scroll-lock', shouldLock);
    }

    syncModalIsolation() {
      const shouldIsolate = this.isOpen && this.currentMode === 'sheet';
      const reviewIsActive = document.querySelector('[data-vcc-review-overlay][data-open="true"], [data-vcc-review-overlay][data-open="opening"]');
      const trustIsActive = Boolean(
        this.trustRoot &&
        !this.trustRoot.hidden &&
        this.trustRoot.getAttribute('aria-hidden') !== 'true'
      );
      if (shouldIsolate && trustIsActive) {
        this.restoreBackground();
        return;
      }
      if (shouldIsolate && reviewIsActive && !this.backgroundState.length) return;
      if (shouldIsolate) this.makeBackgroundInert();
      else this.restoreBackground();
    }

    makeBackgroundInert() {
      if (this.backgroundState.length) return;

      const backgroundState = [];
      const isolateElement = (element) => {
        backgroundState.push({
          element,
          inert: element.inert,
          ariaHidden: element.getAttribute('aria-hidden')
        });
        element.inert = true;
        element.setAttribute('aria-hidden', 'true');
      };

      if (this.compact) isolateElement(this.compact);
      let branch = this.root;

      while (branch && branch !== document.body) {
        const parent = branch.parentElement;
        if (!parent) break;

        [...parent.children].forEach((element) => {
          if (
            element === branch ||
            element.matches('script, style, link, [data-vcc-review-overlay]')
          ) return;

          isolateElement(element);
        });

        branch = parent;
      }

      this.backgroundState = backgroundState;
    }

    restoreBackground() {
      const releasedState = this.backgroundState;
      releasedState.forEach(({ element, inert, ariaHidden }) => {
        if (!element.isConnected) return;
        element.inert = inert;
        if (ariaHidden === null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', ariaHidden);
      });
      this.backgroundState = [];
      window.VCCReviewViewer?.rebaseBackground?.(releasedState);
    }

    revealDelay() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;
      const raw = window.getComputedStyle(this.root).getPropertyValue('--vcc-af-duration').trim();
      const duration = Number.parseFloat(raw);
      if (!Number.isFinite(duration)) return 0;
      return Math.min(1600, raw.endsWith('ms') ? duration : duration * 1000) + 30;
    }

    scheduleOpenFocus(target = null) {
      window.clearTimeout(this.focusTimer);
      const delay = this.currentMode === 'sheet' ? 0 : this.revealDelay();
      this.focusTimer = window.setTimeout(() => {
        if (!this.isOpen || this.privacyDialog?.open) return;
        if (!target && this.drawer.contains(document.activeElement)) return;

        const focusTarget = target || this.root.querySelector('[data-vcc-af-primary-close]') || this.closers[0];
        if (!focusTarget?.isConnected) return;
        focusTarget.focus({ preventScroll: this.currentMode === 'sheet' });
        if (this.currentMode === 'inline' || target) {
          focusTarget.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
        }
      }, delay);
    }

    syncBackdropState() {
      if (!this.backdrop) return;
      const isActive = this.isOpen && this.currentMode === 'sheet';
      this.backdrop.disabled = !isActive;
      this.backdrop.setAttribute('aria-hidden', 'true');
    }

    onKeydown(event) {
      if (!this.isOpen || this.privacyDialog?.open) return;
      const reviewOverlay = document.querySelector('[data-vcc-review-overlay]:not([hidden])');
      if (reviewOverlay) return;
      if (this.trustRoot && !this.trustRoot.hidden && this.trustRoot.getAttribute('aria-hidden') !== 'true') return;
      if (this.currentMode !== 'sheet' && !this.root.contains(event.target)) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab' || this.currentMode !== 'sheet') return;
      this.trapFocus(event);
    }

    trapFocus(event) {
      const focusable = [...this.drawer.querySelectorAll(FOCUSABLE_SELECTOR)]
        .filter((element) => !element.hasAttribute('hidden') && element.offsetParent !== null);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeElement = document.activeElement;

      if (!this.drawer.contains(activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      if (event.shiftKey && activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onLinkClick(event) {
      const link = event.target.closest('a[href]');
      if (!link || !this.root.contains(link)) return;
      if (this.privacyDialog?.contains(link)) {
        this.closePrivacy(false, false);
        this.retirePrivacyMarker();
      }
      if (link.matches('[data-vcc-af-review-trigger], [data-vcc-open-reviews], [data-vcc-af-trust-trigger]')) return;
      if (this.root.dataset.closeOnLink !== 'true') return;
      this.retireCurrentHistoryMarker();
      this.setOpen(false, { focus: false, manageHistory: false });
    }

    onReviewRequest(event) {
      // Do not prevent the existing delegated data-vcc-open-reviews handler.
      // This event provides an additional integration hook without creating a second viewer.
      this.root.dispatchEvent(new CustomEvent('vcc:advanced-footer:review-request', {
        bubbles: true,
        detail: {
          sectionId: this.root.dataset.sectionId,
          trigger: event.currentTarget
        }
      }));

      window.setTimeout(() => {
        if (window.VCCReviewViewer?.ready || !this.reviewFallbackUrl) return;
        window.location.assign(this.reviewFallbackUrl);
      }, 0);
    }

    loadSharedTrustAssets() {
      if (sharedTrustAssetsPromise) return sharedTrustAssetsPromise;

      sharedTrustAssetsPromise = Promise.all([
        loadSharedTrustStylesheet(this.root.dataset.vccAfTrustStyleUrl),
        loadSharedTrustScript(this.root.dataset.vccAfTrustScriptUrl)
      ]).catch((error) => {
        sharedTrustAssetsPromise = null;
        throw error;
      });

      return sharedTrustAssetsPromise;
    }

    cancelPendingTrustRequest() {
      this.trustRequestToken = null;
      this.trustReplayTarget = null;
      this.trustPending = false;
      this.trustTriggers.forEach((trigger) => trigger.removeAttribute('aria-busy'));
    }

    async onTrustRequest(event) {
      const trigger = event.currentTarget;
      if (this.trustReplayTarget === trigger) {
        this.trustReplayTarget = null;
        return;
      }

      if (!this.trustRoot) return;
      event.preventDefault();
      event.stopPropagation();
      if (trigger.getAttribute('aria-busy') === 'true') return;

      trigger.setAttribute('aria-busy', 'true');
      const requestToken = Symbol('shared-trust-request');
      this.trustRequestToken = requestToken;
      this.trustPending = true;

      try {
        await this.loadSharedTrustAssets();
        const requestIsStale = (
          this.trustRequestToken !== requestToken ||
          !this.isOpen ||
          !trigger.isConnected ||
          !this.trustRoot.isConnected
        );
        if (requestIsStale) {
          if (this.trustRequestToken === requestToken) {
            this.trustRequestToken = null;
            this.trustPending = false;
            this.syncModalIsolation();
          }
          return;
        }
        this.trustRequestToken = null;
        this.restoreBackground();
        this.trustReplayTarget = trigger;
        trigger.click();
        const opened = !this.trustRoot.hidden && this.trustRoot.getAttribute('aria-hidden') !== 'true';
        this.trustPending = false;
        if (opened) return;
        this.syncModalIsolation();
        if (!this.isOpen) return;
        const fallbackUrl = trigger.getAttribute('href');
        if (fallbackUrl) window.location.assign(fallbackUrl);
      } catch (_error) {
        if (this.trustRequestToken !== requestToken) return;
        this.trustRequestToken = null;
        this.trustPending = false;
        this.syncModalIsolation();
        const fallbackUrl = trigger.getAttribute('href');
        if (fallbackUrl) window.location.assign(fallbackUrl);
      } finally {
        trigger.removeAttribute('aria-busy');
      }
    }

    observeTrustDrawer() {
      if (!this.trustRoot || typeof MutationObserver === 'undefined') return;
      this.trustObserver = new MutationObserver(() => {
        const isActive = !this.trustRoot.hidden && this.trustRoot.getAttribute('aria-hidden') !== 'true';
        if (isActive) this.trustPending = false;
        this.syncModalIsolation();
      });
      this.trustObserver.observe(this.trustRoot, {
        attributes: true,
        attributeFilter: ['aria-hidden', 'class', 'hidden']
      });
    }

    bindPrivacy() {
      if (!this.privacyDialog) return;
      const find = (name) => this.privacyDialog.querySelector(`[data-vcc-af-privacy-${name}]`);
      this.privacy = {
        form: find('form'), status: find('status'), close: find('close'),
        reject: find('reject'), save: find('save'), sale: find('sale'), signal: find('signal'),
        fields: [...this.privacyDialog.querySelectorAll('input[type="checkbox"]')], dirty: new Set()
      };
      this.bound.privacyClose = (event) => { event.preventDefault(); this.closePrivacy(); };
      this.bound.privacySave = (event) => { event.preventDefault(); this.savePrivacy(false); };
      this.bound.privacyReject = () => this.savePrivacy(true);
      this.bound.privacyChange = (event) => {
        this.privacy.dirty.add(event.target.name);
        this.setPrivacySaved();
      };
      this.bound.privacyConsent = () => {
        if (this.privacy.saving) return;
        if (this.privacy.savedSnapshot) {
          try {
            const current = this.privacySnapshot(this.privacy.api);
            const previous = this.privacy.savedSnapshot;
            if (current.gpc !== previous.gpc || Object.keys(PRIVACY_PERMISSIONS).some((key) =>
              current.recorded[key] !== previous.recorded[key] || current.allowed[key] !== previous.allowed[key])) {
              this.setPrivacySaved();
            }
          } catch (_error) { this.setPrivacySaved(); }
        }
        if (this.privacyDialog.open && !this.privacy.saving && !this.privacy.dirty.size) this.refreshPrivacy();
      };
      this.bound.privacyPop = (event) => {
        this.privacyPendingBack = false;
        if (event.state?.[PRIVACY_HISTORY_KEY] === this.historyId) {
          if (!this.privacyDialog.open) this.retirePrivacyMarker();
        } else if (this.privacyDialog.open) this.closePrivacy(false);
      };
      this.cookieButtons.forEach((button) => {
        button.setAttribute('data-vcc-af-cookie-label', button.textContent.trim() || 'Cookie Preferences');
        button.setAttribute('aria-haspopup', 'dialog');
        button.setAttribute('aria-controls', this.privacyDialog.id);
        button.setAttribute('aria-expanded', 'false');
      });
      this.privacyDialog.addEventListener('cancel', this.bound.privacyClose);
      this.privacy.close.addEventListener('click', this.bound.privacyClose);
      this.privacy.form.addEventListener('submit', this.bound.privacySave);
      this.privacy.form.addEventListener('change', this.bound.privacyChange);
      this.privacy.reject.addEventListener('click', this.bound.privacyReject);
      document.addEventListener('visitorConsentCollected', this.bound.privacyConsent);
      window.addEventListener('popstate', this.bound.privacyPop, { capture: true });
      this.retirePrivacyMarker();
    }

    setPrivacySaved(snapshot = null) {
      this.privacy.savedSnapshot = snapshot;
      this.cookieButtons.forEach((button) => {
        const label = button.getAttribute('data-vcc-af-cookie-label') || 'Cookie Preferences';
        button.textContent = snapshot ? `${label} Saved` : label;
        button.setAttribute('data-vcc-af-cookie-saved', snapshot ? 'true' : 'false');
      });
      this.root.querySelectorAll('[data-vcc-af-cookie-status]').forEach((status) => { status.textContent = ''; });
      if (snapshot && this.privacy.launcherStatus) this.privacy.launcherStatus.textContent = 'Cookie preferences saved.';
    }

    retirePrivacyMarker() {
      if (window.history.state?.[PRIVACY_HISTORY_KEY] !== this.historyId) return;
      const state = { ...window.history.state };
      delete state[PRIVACY_HISTORY_KEY];
      try { window.history.replaceState(state, '', window.location.href); } catch (_error) { /* No navigation. */ }
    }

    onCookiePreferences(event) {
      event.preventDefault();
      event.stopPropagation();
      if (!this.privacy || this.privacyDialog.open || this.privacyPendingBack) return;
      this.privacy.opener = event.currentTarget;
      this.privacy.launcherStatus = event.currentTarget.closest('nav')?.querySelector('[data-vcc-af-cookie-status]')
        || this.root.querySelector('[data-vcc-af-cookie-status]');
      this.setPrivacySaved();
      try { this.privacyDialog.showModal(); } catch (_error) {
        if (this.privacy.launcherStatus) this.privacy.launcherStatus.textContent = PRIVACY_UNAVAILABLE_MESSAGE;
        return;
      }
      this.privacy.opener.setAttribute('aria-expanded', 'true');
      const state = window.history.state && typeof window.history.state === 'object' ? window.history.state : {};
      try {
        window.history.pushState({ ...state, [PRIVACY_HISTORY_KEY]: this.historyId }, '', window.location.href);
      } catch (_error) { /* The close control remains available if History is unavailable. */ }
      this.privacy.close.focus({ preventScroll: true });
      this.refreshPrivacy();
    }

    closePrivacy(manageHistory = true, focus = true) {
      if (!this.privacyDialog?.open) return;
      if (manageHistory && window.history.state?.[PRIVACY_HISTORY_KEY] === this.historyId) {
        if (this.privacyPendingBack) return;
        this.privacyPendingBack = true;
        try { window.history.back(); return; } catch (_error) { this.privacyPendingBack = false; }
      }
      this.privacyToken = null;
      this.privacyPendingBack = false;
      this.privacy.saving = false;
      this.retirePrivacyMarker();
      this.privacyDialog.close();
      this.privacy.opener?.setAttribute('aria-expanded', 'false');
      if (focus && this.privacy.opener?.isConnected && !this.privacy.opener.closest('[inert]')) {
        this.privacy.opener.focus({ preventScroll: true });
      }
    }

    privacySnapshot(api) {
      if (typeof api?.currentVisitorConsent !== 'function' || typeof api.setTrackingConsent !== 'function'
        || typeof api.saleOfDataRegion !== 'function') throw new Error('Privacy permissions are unavailable.');
      const recorded = api.currentVisitorConsent();
      const allowed = {};
      const values = {};
      Object.entries(PRIVACY_PERMISSIONS).forEach(([key, method]) => {
        if (!recorded || !['', 'yes', 'no'].includes(recorded[key]) || typeof api[method] !== 'function') {
          throw new Error('Privacy permissions are incomplete.');
        }
        allowed[key] = api[method]();
        if (typeof allowed[key] !== 'boolean') throw new Error('Privacy permissions are unknown.');
        values[key] = recorded[key] !== 'no' && allowed[key];
      });
      const saleRegion = api.saleOfDataRegion();
      if (typeof saleRegion !== 'boolean') throw new Error('Privacy region is unknown.');
      const gpc = navigator.globalPrivacyControl === true;
      if (gpc) values.sale_of_data = false;
      return { recorded, allowed, values, gpc, offerSale: saleRegion || recorded.sale_of_data === 'no' };
    }

    setPrivacyEnabled(enabled) {
      this.privacy.fields.forEach((field) => {
        field.disabled = !enabled || (field.name === 'sale_of_data' && (this.privacy.gpc || !this.privacy.offerSale));
      });
      this.privacy.save.disabled = !enabled;
      this.privacy.reject.disabled = !enabled;
    }

    privacyFailure(message) {
      this.setPrivacyEnabled(false);
      this.setPrivacySaved();
      this.privacyDialog.setAttribute('data-vcc-af-privacy-state', 'unavailable');
      this.privacy.status.textContent = message;
    }

    async refreshPrivacy() {
      if (!this.privacyDialog.open || this.privacy.saving) return;
      const token = this.privacyToken = Symbol('privacy-read');
      this.setPrivacyEnabled(false);
      this.privacyDialog.setAttribute('data-vcc-af-privacy-state', 'loading');
      this.privacy.status.textContent = 'Loading…';
      this.privacy.dirty.clear();
      // Stop the visible wait early without abandoning the bounded native load.
      const noticeTimer = window.setTimeout(() => {
        if (this.privacyToken === token && this.privacyDialog.open && this.root.isConnected) {
          this.privacyFailure(PRIVACY_UNAVAILABLE_MESSAGE);
        }
      }, 2000);
      try {
        const api = await loadCustomerPrivacy();
        if (this.privacyToken !== token || !this.privacyDialog.open || !this.root.isConnected) return;
        const snapshot = this.privacySnapshot(api);
        Object.assign(this.privacy, { api, gpc: snapshot.gpc, offerSale: snapshot.offerSale });
        this.privacy.fields.forEach((field) => { field.checked = snapshot.values[field.name]; });
        this.privacy.sale.hidden = !snapshot.offerSale;
        this.privacy.signal.hidden = !snapshot.gpc;
        this.privacy.status.textContent = '';
        this.privacyDialog.setAttribute('data-vcc-af-privacy-state', 'ready');
        this.setPrivacyEnabled(true);
      } catch (_error) {
        if (this.privacyToken === token && this.privacyDialog.open) {
          this.privacyFailure(PRIVACY_UNAVAILABLE_MESSAGE);
        }
      } finally {
        window.clearTimeout(noticeTimer);
      }
    }

    async savePrivacy(rejectOptional) {
      if (!this.privacyDialog.open || this.privacy.saving || this.privacy.save.disabled) return;
      const token = this.privacyToken = Symbol('privacy-save');
      this.setPrivacySaved();
      this.privacy.saving = true;
      this.privacyDialog.setAttribute('data-vcc-af-privacy-state', 'saving');
      this.setPrivacyEnabled(false);
      this.privacy.status.textContent = 'Saving…';
      try {
        const before = this.privacySnapshot(this.privacy.api);
        const consent = {};
        this.privacy.fields.forEach((field) => {
          if (field.name === 'sale_of_data') {
            if (before.offerSale && (rejectOptional || before.gpc || this.privacy.dirty.has(field.name))) {
              consent.sale_of_data = !rejectOptional && !before.gpc && field.checked;
            }
          } else {
            // Do not overwrite a refusal collected elsewhere while this panel was being edited.
            consent[field.name] = !rejectOptional && field.checked
              && !(before.recorded[field.name] === 'no' && !this.privacy.dirty.has(field.name));
          }
        });
        await new Promise((resolve, reject) => {
          const timeout = window.setTimeout(() => reject(new Error('Save confirmation timed out.')), 8000);
          try {
            this.privacy.api.setTrackingConsent(consent, (result) => {
              window.clearTimeout(timeout);
              if (result?.error) reject(new Error('Shopify did not save these choices.'));
              else resolve();
            });
          } catch (error) { window.clearTimeout(timeout); reject(error); }
        });
        if (this.privacyToken !== token || !this.privacyDialog.open || !this.root.isConnected) return;
        const saved = this.privacySnapshot(this.privacy.api);
        if (Object.entries(consent).some(([key, value]) => saved.recorded[key] !== (value ? 'yes' : 'no')
          || saved.allowed[key] !== value)) throw new Error('Saved choices could not be confirmed.');
        this.setPrivacySaved(saved);
        this.privacy.saving = false;
        this.closePrivacy();
      } catch (_error) {
        if (this.privacyToken === token && this.privacyDialog.open) {
          this.privacy.saving = false;
          this.privacyFailure('Your choices could not be confirmed.');
        }
      }
    }

    onThemeEvent(event) {
      const requested = event?.detail?.theme || event?.detail?.mode || event?.detail?.colorScheme;
      if (requested === 'dark' || requested === 'light') {
        this.applyTheme(requested);
      } else {
        this.resolveTheme();
      }
    }

    resolveTheme() {
      const configured = this.root.dataset.themeMode || 'inherit';
      if (configured === 'light' || configured === 'dark') {
        this.applyTheme(configured);
        return;
      }

      const html = document.documentElement;
      const body = document.body;
      const vccTheme = window.VccGlobalTheme?.getTheme?.() || html.getAttribute('data-vcc-theme');
      if (vccTheme === 'dark' || vccTheme === 'light') {
        this.applyTheme(vccTheme);
        return;
      }

      const candidates = [
        html.dataset.theme,
        html.dataset.colorScheme,
        html.dataset.mode,
        body?.dataset.theme,
        body?.dataset.colorScheme,
        body?.dataset.mode
      ].filter(Boolean).map((value) => String(value).toLowerCase());

      const classText = `${html.className || ''} ${body?.className || ''}`.toLowerCase();
      let resolved;

      if (candidates.some((value) => value.includes('dark')) || /(^|\s)(dark|theme-dark|dark-mode)(\s|$)/.test(classText)) {
        resolved = 'dark';
      } else if (candidates.some((value) => value.includes('light')) || /(^|\s)(light|theme-light|light-mode)(\s|$)/.test(classText)) {
        resolved = 'light';
      } else {
        resolved = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      }

      this.applyTheme(resolved);
    }

    applyTheme(theme) {
      this.root.dataset.resolvedTheme = theme === 'dark' ? 'dark' : 'light';
    }

    observeTheme() {
      if (this.root.dataset.themeMode !== 'inherit' || typeof MutationObserver === 'undefined') return;
      this.themeObserver = new MutationObserver(() => this.resolveTheme());
      this.themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class', 'data-vcc-theme', 'data-theme', 'data-color-scheme', 'data-mode']
      });
      if (document.body) {
        this.themeObserver.observe(document.body, {
          attributes: true,
          attributeFilter: ['class', 'data-theme', 'data-color-scheme', 'data-mode']
        });
      }
    }

    destroy() {
      this.cookieButtons.forEach((button) => button.removeEventListener('click', this.bound.cookiePreferences));
      if (this.privacy) {
        this.closePrivacy(false, false);
        this.setPrivacySaved();
        this.retirePrivacyMarker();
        this.privacyToken = null;
        this.privacyDialog.removeEventListener('cancel', this.bound.privacyClose);
        this.privacy.close.removeEventListener('click', this.bound.privacyClose);
        this.privacy.form.removeEventListener('submit', this.bound.privacySave);
        this.privacy.form.removeEventListener('change', this.bound.privacyChange);
        this.privacy.reject.removeEventListener('click', this.bound.privacyReject);
        document.removeEventListener('visitorConsentCollected', this.bound.privacyConsent);
        window.removeEventListener('popstate', this.bound.privacyPop, { capture: true });
      }

      if (this.drawerEnabled) {
        this.openers.forEach((button) => button.removeEventListener('click', this.bound.open));
        this.closers.forEach((button) => button.removeEventListener('click', this.bound.close));
        this.backdrop?.removeEventListener('click', this.bound.close);
        document.removeEventListener('keydown', this.bound.keydown);
        this.root.removeEventListener('click', this.bound.linkClick);
        this.reviewTriggers.forEach((button) => button.removeEventListener('click', this.bound.reviewRequest));
        this.trustTriggers.forEach((link) => link.removeEventListener('click', this.bound.trustRequest));

        if (this.media.removeEventListener) this.media.removeEventListener('change', this.bound.resize);
        else this.media.removeListener(this.bound.resize);

        document.removeEventListener('vcc:review-viewer:close', this.bound.reviewClose);
        window.removeEventListener('popstate', this.bound.popstate, { capture: true });
      }

      document.removeEventListener('vcc:theme-change', this.bound.themeEvent);
      document.removeEventListener('vcc:site-theme-change', this.bound.themeEvent);
      document.removeEventListener('theme:change', this.bound.themeEvent);
      document.removeEventListener('color-scheme:change', this.bound.themeEvent);
      this.themeObserver?.disconnect();
      this.trustObserver?.disconnect();
      window.clearTimeout(this.focusTimer);
      this.retireCurrentHistoryMarker();
      this.historyTraversalPending = false;
      this.ownsHistoryEntry = false;
      this.restoreBackground();
      document.documentElement.classList.remove('vcc-af-scroll-lock');
      document.body?.classList.remove('vcc-af-scroll-lock');
      this.pageWrapper?.classList.remove('vcc-af-scroll-lock');
      delete this.root.vccAdvancedFooter;
    }
  }

  function getRoots(root = document) {
    const matches = root instanceof Element && root.matches(ROOT_SELECTOR) ? [root] : [];
    return [...matches, ...root.querySelectorAll(ROOT_SELECTOR)];
  }

  function init(root = document) {
    getRoots(root).forEach((element) => {
      if (element.vccAdvancedFooter) return;
      element.vccAdvancedFooter = new VccAdvancedFooter(element);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    getRoots(event.target).forEach((element) => element.vccAdvancedFooter?.destroy());
  });

  window.VccAdvancedFooter = { init };
})();
